import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchListingBySlug } from '../lib/api';
import type { Listing } from '../types';

export default function ManageListing() {
  const { slug } = useParams<{ slug: string }>();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }
    fetchListingBySlug(slug)
      .then((l) => {
        setListing(l);
      })
      .catch(() => {
        setListing(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  const handleClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!listing || !email) return;
    setSending(true);
    try {
      await fetch('/.netlify/functions/claim-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: listing.id,
          name: email.split('@')[0],
          email,
          message: 'Claimed via manage page',
        }),
      });
      setSent(true);
    } catch {
      // ignore
    }
    setSending(false);
  };

  if (loading) {
    return <div className="text-center py-24 text-text-muted">Loading...</div>;
  }

  if (!listing) {
    return (
      <div className="max-w-[600px] mx-auto px-6 py-24 text-center">
        <h1 className="text-2xl font-bold mb-3">Listing not found</h1>
        <p className="text-text-secondary mb-6">We couldn't find that listing.</p>
        <Link to="/" className="btn btn-primary">Back to MarineLink</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[700px] mx-auto px-4 md:px-8 py-8 md:py-12">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-muted mb-6">
        <Link to="/" className="text-text-secondary hover:text-accent">Home</Link>
        <span>/</span>
        <Link to={`/listing/${listing.category_slug || listing.category_id}/${listing.slug}`} className="text-text-secondary hover:text-accent">
          {listing.title}
        </Link>
        <span>/</span>
        <span>Manage</span>
      </div>

      <h1 className="font-display text-2xl font-bold mb-2">Manage Your Listing</h1>
      <p className="text-text-secondary text-sm mb-8">
        You're managing <strong>{listing.title}</strong> on MarineLink.co.nz
      </p>

      {/* Current listing preview */}
      <div className="border border-[rgba(16,42,66,0.12)] rounded-xl overflow-hidden mb-8">
        <div className="bg-surface-light dark:bg-surface px-5 py-4 border-b border-[rgba(16,42,66,0.12)]">
          <div className="text-xs text-accent font-semibold uppercase tracking-wide">{listing.category_name}</div>
          <div className="font-bold text-lg mt-1">{listing.title}</div>
          <div className="text-sm text-text-muted">📍 {listing.region_name}, New Zealand</div>
        </div>
        <div className="p-5">
          <p className="text-sm text-text-secondary line-clamp-3 mb-4">{listing.description}</p>
          {listing.services && listing.services.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {listing.services.map((s: string, i: number) => (
                <span key={i} className="px-3 py-1 bg-card-light dark:bg-card border border-[rgba(16,42,66,0.12)] rounded-full text-xs text-text-secondary">{s}</span>
              ))}
            </div>
          )}
          <div className="flex gap-3 flex-wrap">
            {listing.phone && <span className="text-sm">📞 {listing.phone}</span>}
            {listing.email && <span className="text-sm">✉️ {listing.email}</span>}
            {listing.website && <a href={listing.website} target="_blank" rel="noopener" className="text-sm text-accent">🌐 Website →</a>}
          </div>
        </div>
      </div>

      {/* Claim form */}
      {!sent ? (
        <div className="border border-[rgba(16,42,66,0.12)] rounded-xl p-6 mb-8">
          <h2 className="font-semibold text-lg mb-2">Claim this listing</h2>
          <p className="text-text-secondary text-sm mb-4">Enter your email to verify ownership. We'll send you a link to edit your listing, add photos, and update your details.</p>
          <form onSubmit={handleClaim} className="flex gap-3">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.co.nz"
              className="form-input flex-1"
            />
            <button type="submit" disabled={sending} className="btn btn-primary">
              {sending ? 'Sending...' : 'Claim listing'}
            </button>
          </form>
        </div>
      ) : (
        <div className="border border-green/30 bg-green/5 rounded-xl p-6 mb-8 text-center">
          <div className="text-2xl mb-2">✅</div>
          <h2 className="font-semibold text-lg mb-2">Claim request sent!</h2>
          <p className="text-text-secondary text-sm">Check your email ({email}) for a link to manage your listing.</p>
        </div>
      )}

      {/* Upgrade options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="border border-[rgba(16,42,66,0.12)] rounded-xl p-5">
          <div className="text-xs text-accent font-semibold uppercase tracking-wide mb-1">Standard</div>
          <div className="text-2xl font-bold mb-1">$9<span className="text-sm font-normal text-text-muted">/month</span></div>
          <ul className="text-sm text-text-secondary space-y-1 mt-3">
            <li>✓ Full profile with photos</li>
            <li>✓ Phone, email & website</li>
            <li>✓ Priority search placement</li>
            <li>✓ SEO backlink from MarineLink</li>
            <li>✓ Respond to reviews</li>
          </ul>
          <Link to="/pricing" className="btn btn-ghost btn-sm btn-block mt-4">Choose Standard</Link>
        </div>
        <div className="border-2 border-accent rounded-xl p-5 relative">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-white text-xs font-bold uppercase tracking-wide px-3 py-0.5 rounded-full">Best Value</div>
          <div className="text-xs text-accent font-semibold uppercase tracking-wide mb-1">Premium</div>
          <div className="text-2xl font-bold mb-1">$19<span className="text-sm font-normal text-text-muted">/month</span></div>
          <ul className="text-sm text-text-secondary space-y-1 mt-3">
            <li>✓ Everything in Standard</li>
            <li>✓ Top-of-search placement</li>
            <li>✓ Editable listing page</li>
            <li>✓ Lead capture form</li>
            <li>✓ Do-follow backlink (max SEO)</li>
            <li>✓ Analytics dashboard</li>
          </ul>
          <Link to="/pricing" className="btn btn-primary btn-sm btn-block mt-4">Choose Premium</Link>
        </div>
      </div>

      {/* Website rebuild offer */}
      <div className="border border-[rgba(16,42,66,0.12)] rounded-xl p-6 mb-8 bg-surface-light dark:bg-surface">
        <h2 className="font-semibold text-lg mb-2">Need a new website?</h2>
        <p className="text-text-secondary text-sm mb-3">We build modern marine websites. You own it outright - no lock-in.</p>
        <ul className="text-sm space-y-1 mb-4">
          <li><strong>$500 one-time</strong> - you own the website outright</li>
          <li><strong>Free hosting</strong> for the first year</li>
          <li><strong>$180/year</strong> from year two (optional)</li>
        </ul>
        <Link to="/contact?topic=website" className="btn btn-ghost btn-sm">Enquire about a website →</Link>
      </div>

      {/* Contact */}
      <div className="text-center text-sm text-text-muted">
        Questions? Email <a href="mailto:phil@marinelink.co.nz" className="text-accent">phil@marinelink.co.nz</a> or call <strong>027 251 5002</strong>
      </div>
    </div>
  );
}